import {Extension, type Editor} from "@tiptap/core";
import {PluginKey} from "@tiptap/pm/state";
import {DEFAULT_CONTENT_LANGUAGE} from "../model/resolution";
import type {VariableCatalogue} from "../model/types";
import {VARIABLE_NODE_NAME} from "../model/variableToken";
import {matchesPickerItem, pickerItemsFor, type VariablePickerItem} from "../picker/pickerItem";
import {insertVariable} from "./insertVariable";
import {createVariableCatalogueStore, type VariableCatalogueStore} from "./variableCatalogueStore";
import {variablePickerPlugins} from "./variablePickerPlugins";

export interface VariableSuggestionOptions {
    /** The editor's catalogue, shared with the node. */
    store?: VariableCatalogueStore;
    /** Ghost text after the `/` while nothing has been typed yet; the document is the search field. */
    hint?: string;
    /** The popup's search header. Off where the document already shows what was typed. */
    search?: boolean;
    /** Names this suggestion's plugin; one is made per editor when it is left out. */
    pluginKey?: PluginKey;
}

export interface VariableSuggestionStorage {
    /** Where `isVariablePickerOpen` finds this editor's suggestion state. */
    pluginKey: PluginKey;
}

const EMPTY_CATALOGUE: VariableCatalogue = {variables: [], preview: {language: DEFAULT_CONTENT_LANGUAGE}};

/*
    Built once per catalogue object, then only filtered while the author types: the items carry
    their groups, examples and sources, and a swapped catalogue is a new object, so it rebuilds.
 */
const itemsByCatalogue = new WeakMap<VariableCatalogue, VariablePickerItem[]>();

const pickerItemsOf = (catalogue: VariableCatalogue) => {
    let items = itemsByCatalogue.get(catalogue);
    if (!items) {
        items = pickerItemsFor(catalogue.variables, catalogue.preview);
        itemsByCatalogue.set(catalogue, items);
    }

    return items;
};

/** Typing `/` opens the picker; `allowSpaces: false` and the default prefixes keep `24/7` prose. */
export const VariableSuggestion = Extension.create<VariableSuggestionOptions, VariableSuggestionStorage>({
    name: "variableSuggestion",

    addOptions() {
        return {store: undefined, hint: undefined, search: undefined, pluginKey: undefined};
    },

    addStorage() {
        return {pluginKey: this.options.pluginKey ?? new PluginKey("variableSuggestion")};
    },

    addProseMirrorPlugins() {
        // Configured by `variableExtensions`; on its own it follows the node's store, if the node is there.
        const nodeStorage = (this.editor.storage as unknown as Record<string, {store?: VariableCatalogueStore} | undefined>)[
            VARIABLE_NODE_NAME
        ];
        const store = this.options.store ?? nodeStorage?.store ?? createVariableCatalogueStore(EMPTY_CATALOGUE);

        return variablePickerPlugins<VariablePickerItem>(
            {
                editor: this.editor,
                pluginKey: this.storage.pluginKey,
                char: "/",
                allowSpaces: false,
                startOfLine: false,
                allow: ({editor}) => editor.isEditable && store.get().variables.length > 0,
                decorationClass: "variable-suggestion",
                decorationEmptyClass: "variable-suggestion-empty",
                decorationContent: this.options.hint ?? "",

                items: ({query}) => pickerItemsOf(store.get()).filter(item => matchesPickerItem(item, query)),

                command: ({editor, range, props}) => {
                    insertVariable(editor, props.definition, range);
                },
            },
            {search: this.options.search},
        );
    },
});

/**
 * Whether the `/` popup is open. Without a key it asks this editor's `VariableSuggestion`; a host
 * that runs its own suggestion through `variablePickerPlugins` passes that suggestion's key.
 */
export function isVariablePickerOpen(editor: Editor, pluginKey?: PluginKey): boolean {
    const storages = editor.storage as unknown as Record<string, Partial<VariableSuggestionStorage> | undefined>;
    const key = pluginKey ?? storages[VariableSuggestion.name]?.pluginKey;

    return !!(key?.getState(editor.state) as {active?: boolean} | undefined)?.active;
}

export default VariableSuggestion;
